import React, { useEffect, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Phone, MessageCircle, Star } from 'lucide-react';
import DriverBadgeChips from '../../components/DriverBadgeChips';

const DriverInfoCard = ({ ride, status, onShowChat }) => {
  const [badges, setBadges] = useState([]);

  useEffect(() => {
    const driverId = ride?.driver?.id || ride?.driverId;
    if (!driverId) { setBadges([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const api = (await import('../../utils/api')).default;
        const { data } = await api.get(`/drivers/${driverId}/public`);
        if (!cancelled) setBadges(data?.badges || []);
      } catch {
        if (!cancelled) setBadges([]);
      }
    })();
    return () => { cancelled = true; };
  }, [ride?.driver?.id, ride?.driverId]);

  if (!ride?.driver) {
    if (status === 'pending') {
      return (
        <Card className="p-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-gray-200 animate-pulse"></div>
            <div className="flex-1">
              <div className="h-5 w-32 bg-gray-200 rounded animate-pulse mb-2"></div>
              <div className="h-4 w-48 bg-gray-200 rounded animate-pulse"></div>
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-4 text-center">Looking for nearby drivers...</p>
        </Card>
      );
    }
    return null;
  }

  return (
    <Card className="p-6 bg-gradient-to-r from-pink-50 to-purple-50 border-pink-200">
      <h3 className="text-sm text-gray-600 mb-3">Your Driver</h3>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center text-white text-2xl font-bold shadow-lg">
            {ride.driver.name?.charAt(0) || 'D'}
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900">{ride.driver.name}</h3>
            <div className="flex items-center gap-2 text-gray-600 mt-1">
              <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
              <span>{ride.driver.rating || '5.0'}</span>
              <span className="text-gray-400">•</span>
              <span>{ride.driver.rides || 0} rides</span>
            </div>
            {ride.driver.vehicle && typeof ride.driver.vehicle === 'object' && (
              <p className="text-sm text-gray-700 mt-1" data-testid="driver-vehicle-info">
                <span className="font-medium">
                  {[ride.driver.vehicle.color, ride.driver.vehicle.make, ride.driver.vehicle.model].filter(Boolean).join(' ')}
                </span>
                {ride.driver.vehicle.plate && (
                  <>
                    <span className="mx-2">|</span>
                    <span className="bg-gray-200 px-2 py-0.5 rounded font-mono">{ride.driver.vehicle.plate}</span>
                  </>
                )}
              </p>
            )}
            {ride.driver.vehicleType && !ride.driver.vehicle && (
              <p className="text-sm text-gray-600 mt-1">{ride.driver.vehicleType}</p>
            )}
            {ride.driver.phone && (
              <p className="text-sm text-pink-600 mt-1">{ride.driver.phone}</p>
            )}
            <DriverBadgeChips badges={badges} />
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <p className="text-2xl font-bold text-gray-900">K{ride.price?.toFixed(2)}</p>
          <div className="flex gap-2">
            {ride.driver.phone && (
              <a href={`tel:${ride.driver.phone}`}>
                <Button variant="outline" size="sm" className="bg-green-50 border-green-300 hover:bg-green-100">
                  <Phone className="h-4 w-4 text-green-600" />
                </Button>
              </a>
            )}
            <Button variant="outline" size="sm" onClick={onShowChat}>
              <MessageCircle className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default DriverInfoCard;
